import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import type { SingleValue } from "react-select";

import { Meta } from "@/@types/paginated";
import { Input } from "@/components/ui/input";
import { useGetStoreUsersQuery } from "@/services/store-access-query";
import { getSelectClasses } from "@/utils/filter-styles";
import { SelectInput } from "components/Select";

import { useStoreAccessFilterActions } from "../../filter/hooks/actions";
import { useStoreAccessFilterState } from "../../filter/hooks/filterState";
import { extractErrorMessage, isStoreConnectionError } from "../../hooks/utils";
import { DataTable } from "../shared/data-table";

import {
  CLASSIFICATION_OPTIONS,
  ClassificationOption,
  SITUATION_OPTIONS,
  SituationOption,
} from "./types";
import { useUserColumns } from "./use-user-columns";

export default function UsersTab() {
  const { storeCode, users } = useStoreAccessFilterState();
  const { updateTabFilter } = useStoreAccessFilterActions();

  const columns = useUserColumns({ storeCode: storeCode ?? 0 });

  const {
    data: accessData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetStoreUsersQuery(
    {
      storeCode: storeCode!,
      search: users.search || undefined,
      code: users.code || undefined,
      classification: users.classification || undefined,
      cpfFilter: users.cpfFilter,
      blocked: users.blocked ?? undefined,
      situation: users.situation || undefined,
      sortBy: users.sortBy,
      sortOrder: users.sortOrder,
      page: users.page,
      limit: users.perPage,
    },
    { skip: !storeCode },
  );

  const { data = [], meta = {} } = accessData ?? {};

  const connectionError =
    isError && isStoreConnectionError(error)
      ? extractErrorMessage(error) ||
        `Loja ${storeCode} não está acessível no momento.`
      : undefined;

  // Debounce de busca por código — recriado quando `storeCode` muda para que
  // digitações pendentes sejam canceladas após troca de loja.
  const debouncedUpdateCode = useMemo(
    () =>
      debounce((value: string) => {
        updateTabFilter({ tab: "users", key: "code", value });
        updateTabFilter({ tab: "users", key: "page", value: 1 });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeCode],
  );
  useEffect(() => () => debouncedUpdateCode.cancel(), [debouncedUpdateCode]);

  const selectedClassification = useMemo(
    () =>
      CLASSIFICATION_OPTIONS.find(opt => opt.value === users.classification) ||
      null,
    [users.classification],
  );

  const handleClassificationChange = (
    option: SingleValue<ClassificationOption>,
  ) => {
    updateTabFilter({
      tab: "users",
      key: "classification",
      value: option?.value ?? null,
    });
    updateTabFilter({ tab: "users", key: "page", value: 1 });
  };

  const selectedSituation = useMemo(
    () =>
      SITUATION_OPTIONS.find(opt => opt.value === users.situation) || null,
    [users.situation],
  );

  const handleSituationChange = (option: SingleValue<SituationOption>) => {
    updateTabFilter({
      tab: "users",
      key: "situation",
      value: option?.value ?? null,
    });
    updateTabFilter({ tab: "users", key: "page", value: 1 });
  };

  const tabFilters = (
    <>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Código</span>
        <Input
          key={storeCode ?? "no-store"}
          placeholder="Ex: 1234"
          defaultValue={users.code}
          inputMode="numeric"
          onChange={e => debouncedUpdateCode(e.target.value)}
          className="h-8 w-[180px]"
          aria-label="Buscar usuário por código"
          data-testid="users-tab-filters"
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Classificação</span>
        <SelectInput
          options={CLASSIFICATION_OPTIONS}
          placeholder="Todas"
          value={selectedClassification}
          onChange={handleClassificationChange}
          className={`${getSelectClasses(!!users.classification)} h-8 min-w-[180px]`}
          classNamePrefix="an-simple-select"
          isClearable
          aria-label="Filtrar usuários por classificação"
        />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-muted-foreground">Situação</span>
        <SelectInput
          options={SITUATION_OPTIONS as unknown as SituationOption[]}
          placeholder="Todas"
          value={selectedSituation}
          onChange={handleSituationChange}
          className={`${getSelectClasses(!!users.situation)} h-8 min-w-[180px]`}
          classNamePrefix="an-simple-select"
          isClearable
          aria-label="Filtrar usuários por situação"
        />
      </div>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      meta={meta as Meta}
      isLoading={isLoading}
      connectionError={connectionError}
      onRetry={connectionError ? refetch : undefined}
      filterSlot={tabFilters}
    />
  );
}
